import type { SessionHistoryResponse } from '@pomodingo/api-client';
import { apiFetch, apiJson } from './api';
import { getVisitorId } from './visitor';

export type { SessionHistoryResponse };

function getDurationForStage(stage: 'focus' | 'shortBreak' | 'longBreak'): number {
  const settings = JSON.parse(
    localStorage.getItem('pomodoroAppState') ?? '{}',
  ) as { settings?: Record<string, number> };
  const s = settings.settings ?? {};
  const map: Record<string, number | undefined> = {
    focus: s.focusDuration,
    shortBreak: s.shortBreakDuration,
    longBreak: s.longBreakDuration,
  };
  return map[stage] ?? 0;
}

/**
 * Fire-and-forget recording of a completed timer stage. Never throws — the
 * timer must keep working when history recording fails (e.g. offline).
 */
export function recordCompletedStage(
  stage: 'focus' | 'shortBreak' | 'longBreak',
  durationSeconds?: number,
  label?: string,
): void {
  const duration = durationSeconds ?? getDurationForStage(stage);
  if (!duration || duration <= 0) return;

  void apiJson('/api/sessions', {
    method: 'POST',
    body: JSON.stringify({
      visitor_id: getVisitorId(),
      stage,
      duration_seconds: duration,
      ...(label ? { label } : {}),
    }),
  }).catch((error) => {
    console.warn('[Sessions] Failed to record session:', error);
  });
}

export function fetchSessionHistory(): Promise<SessionHistoryResponse> {
  return apiJson<SessionHistoryResponse>(
    `/api/sessions?visitor_id=${encodeURIComponent(getVisitorId())}`,
  );
}

/** Download the signed-in premium user's session history (feature-flagged server-side). */
export async function downloadSessionExport(format: 'json' | 'csv'): Promise<void> {
  const response = await apiFetch(`/api/sessions/export?format=${format}`);
  if (!response.ok) {
    throw new Error(`Export failed: ${response.status}`);
  }
  const blob = await response.blob();
  const disposition = response.headers.get('Content-Disposition') ?? '';
  const match = disposition.match(/filename="([^"]+)"/);
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = match?.[1] ?? `pomodingo-sessions.${format}`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
